// Per-carrier on/off for the rebill generators, and what each one is doing.
//
// WHY THIS SCREEN EXISTS
// ----------------------
// The nightly job was all-or-nothing. When FedEx's legacy generator became
// unsafe ahead of the finops cutover, the only way to stop it was to comment
// out the whole cron entry — which also stopped USPS, the one carrier we
// actually invoice. Three weeks of USPS adjustments went ungenerated as a side
// effect of holding back a different carrier, and nothing showed it.
//
// So this screen has two jobs: switch a single carrier off, and make "is this
// working?" answerable at a glance. Since 2026-10-15 it shows, per generator:
//   * how it is started (daily 06:00 pipeline, after an upload, or by hand),
//   * its last RECORDED run (the 06:00 pipeline and the runner record runs),
//   * what it actually produced (rebills created, pending count and amount,
//     read from customer_rebills — true whichever path created them).
// Only scheduled generators can be "not running"; an upload-driven generator
// that was quiet for a week simply had nothing uploaded.
//
// SAFETY — CLAUDE.md section 3
// ----------------------------
// Switching a generator off decides whether a customer gets billed, so it is
// NOT a bare toggle. The current state is a read-only badge; changing it goes
// through the ⋯ menu, disabling requires a typed reason, and re-enabling
// requires typing the carrier name. Admin/owner only.

import { useState, type ReactNode } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Card } from './Card'
import { Icon } from './Icon'
import { useAuth, authHeaders } from '../lib/auth'

interface Activity {
  last_created_at: string | null
  created_7d: number
  created_30d: number
  pending_count: number
  pending_amount: string
}

interface GeneratorRow {
  carrier: string
  enabled: boolean
  disabled_reason: string | null
  disabled_by: string | null
  disabled_at: string | null
  last_run_at: string | null
  last_skipped_at: string | null
  last_run_status: 'PASS' | 'FAIL' | 'SKIPPED' | null
  last_run_seconds: string | null
  last_run_rebills: number | null
  last_error: string | null
  notes: string | null
  trigger?: string
  scheduled?: boolean
  stale?: boolean
  activity?: Activity
}

const LABEL: Record<string, string> = {
  FEDEX: 'FedEx', DHL: 'DHL', USPS: 'USPS', ASENDIA: 'Asendia', UPS: 'UPS', APC: 'APC',
  EPOST: 'ePost Global', EVRI: 'Evri', INTELCOM: 'Intelcom', UNIUNI: 'UniUni',
  UNUSED: 'Unused labels',
}

function ago(iso: string | null): string {
  if (!iso) return 'never'
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 48) return `${hrs} h ago`
  return `${Math.floor(hrs / 24)} days ago`
}

function money(v: string | number | null | undefined): string {
  const n = Number(v ?? 0)
  const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return (n < 0 ? '−$' : '$') + s
}

// One plain-language status per generator.
function statusOf(g: GeneratorRow): { pill: string; text: string; hint: string } {
  if (!g.enabled) return { pill: 'pill-ink', text: 'Off', hint: 'Creates no rebills until switched back on.' }
  if (g.last_run_status === 'FAIL') return { pill: 'pill-flame', text: 'Last run failed', hint: 'See the error below.' }
  if (g.scheduled) {
    if (g.stale) {
      return g.last_run_at
        ? { pill: 'pill-honey', text: 'Not run in 48 h', hint: 'Scheduled daily, but no run recorded for over two days.' }
        : { pill: 'pill-honey', text: 'No run recorded yet', hint: 'Runs are recorded from the next 06:00 run on.' }
    }
    return { pill: 'pill-mint', text: 'Running daily', hint: 'Daily at 06:00 and after uploads.' }
  }
  if (g.trigger?.startsWith('Manual')) return { pill: 'pill-sky', text: 'On · manual', hint: 'Runs only when started by hand.' }
  return { pill: 'pill-sky', text: 'On · on upload', hint: 'Runs when a file for this carrier is uploaded on the Activity page.' }
}

function Stat({ label, value, sub, tone = 'ink' }: { label: string; value: string; sub?: string; tone?: 'ink' | 'honey' | 'mint' }) {
  const color = tone === 'honey' ? 'text-honey-600' : tone === 'mint' ? 'text-mint-600' : 'text-ink-900'
  return (
    <div className="rounded-lg border border-ink-200 px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">{label}</div>
      <div className={`mt-1 text-[20px] font-semibold ${color}`}>{value}</div>
      {sub && <div className="text-[12px] text-ink-500">{sub}</div>}
    </div>
  )
}

export function RebillGenerators() {
  const qc = useQueryClient()
  const { isAdmin, token } = useAuth()
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery<GeneratorRow[]>({
    queryKey: ['rebill-generators'],
    queryFn: () => fetch('/api/rebill-generators', { headers: authHeaders(token) })
      .then(r => r.json()),
    refetchInterval: 60_000,
  })

  const toggle = useMutation({
    mutationFn: async (a: { carrier: string; enabled: boolean; reason: string }) => {
      const r = await fetch(`/api/rebill-generators/${encodeURIComponent(a.carrier)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body: JSON.stringify({ enabled: a.enabled, reason: a.reason }),
      })
      if (!r.ok) throw new Error((await r.json())?.error || 'Update failed')
      return r.json()
    },
    onSuccess: () => {
      setMenuFor(null); setReason(''); setErrorMsg(null)
      qc.invalidateQueries({ queryKey: ['rebill-generators'] })
    },
    onError: (e: Error) => setErrorMsg(e.message),
  })

  const rows = Array.isArray(data) ? data : []
  const onCount = rows.filter(r => r.enabled).length
  const attention = rows.filter(r => r.enabled && (r.stale || r.last_run_status === 'FAIL')).length
  const pendingCount = rows.reduce((s, r) => s + (r.activity?.pending_count ?? 0), 0)
  const pendingAmount = rows.reduce((s, r) => s + Number(r.activity?.pending_amount ?? 0), 0)
  const created7d = rows.reduce((s, r) => s + (r.activity?.created_7d ?? 0), 0)

  return (
    <Card className="p-6">
      <div className="text-[15px] font-semibold text-ink-900">Rebill generators</div>
      <div className="mt-1 text-[12px] text-ink-500 max-w-3xl">
        Each carrier has a generator that turns carrier invoice charges into customer
        rebills. Rebills are created as <span className="font-medium text-ink-700">Pending</span> —
        nothing reaches a customer until it is pushed or invoiced. Switching a generator off
        loses nothing: the next run after switching it back on picks the shipments up.
      </div>

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Switched on" value={`${onCount} of ${rows.length || 10}`} />
        <Stat label="Needs attention" value={String(attention)} tone={attention ? 'honey' : 'mint'}
              sub={attention ? 'scheduled but not running, or failed' : 'all scheduled generators healthy'} />
        <Stat label="Pending rebills" value={pendingCount.toLocaleString('en-US')} sub={money(pendingAmount)} />
        <Stat label="Created last 7 days" value={created7d.toLocaleString('en-US')} />
      </div>

      {isLoading && <div className="mt-6 text-[13px] text-ink-400">Loading…</div>}
      {isError && <div className="mt-6 text-[13px] text-flame-600">Failed to load generators</div>}

      {rows.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-ink-500 border-b border-ink-200">
                <th className="py-2 pr-4 font-semibold">Carrier</th>
                <th className="py-2 pr-4 font-semibold">Status</th>
                <th className="py-2 pr-4 font-semibold">How it runs</th>
                <th className="py-2 pr-4 font-semibold">Last recorded run</th>
                <th className="py-2 pr-4 font-semibold">Rebills created</th>
                <th className="py-2 pr-4 font-semibold text-right">Pending</th>
                <th className="py-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map(g => {
                const st = statusOf(g)
                const a = g.activity
                return (
                  <FragmentRow key={g.carrier}>
                    <tr className="border-b border-ink-100 align-top">
                      <td className="py-3 pr-4 font-medium text-ink-900 whitespace-nowrap">
                        {LABEL[g.carrier] ?? g.carrier}
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">
                        {/* read-only badge — never a toggle */}
                        <span className={`pill ${st.pill}`} title={st.hint}>{st.text}</span>
                      </td>
                      <td className="py-3 pr-4 text-ink-600">{g.trigger ?? '—'}</td>
                      <td className="py-3 pr-4 text-ink-600 whitespace-nowrap">
                        {g.last_run_at ? (
                          <>
                            <div>{ago(g.last_run_at)}</div>
                            <div className="text-[12px] text-ink-400">
                              <span className={
                                g.last_run_status === 'FAIL' ? 'text-flame-600'
                                : g.last_run_status === 'SKIPPED' ? 'text-ink-400'
                                : 'text-mint-600'
                              }>{g.last_run_status === 'PASS' ? 'OK' : g.last_run_status}</span>
                              {g.last_run_rebills != null && <> · {g.last_run_rebills} new</>}
                            </div>
                          </>
                        ) : g.last_skipped_at ? (
                          <span className="text-ink-400">skipped (off) {ago(g.last_skipped_at)}</span>
                        ) : (
                          <span className="text-ink-400">none recorded</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-ink-600 whitespace-nowrap">
                        {a?.last_created_at ? (
                          <>
                            <div>last {ago(a.last_created_at)}</div>
                            <div className="text-[12px] text-ink-400">
                              {a.created_7d.toLocaleString('en-US')} in 7 days · {a.created_30d.toLocaleString('en-US')} in 30 days
                            </div>
                          </>
                        ) : <span className="text-ink-400">none yet</span>}
                      </td>
                      <td className="py-3 pr-4 text-right whitespace-nowrap">
                        {a && a.pending_count > 0 ? (
                          <>
                            <div className="font-medium text-ink-900">{a.pending_count.toLocaleString('en-US')}</div>
                            <div className="text-[12px] text-ink-500">{money(a.pending_amount)}</div>
                          </>
                        ) : <span className="text-ink-400">—</span>}
                      </td>
                      <td className="py-3 text-right">
                        {isAdmin && (
                          <button
                            className="px-1 text-ink-400 hover:text-ink-700"
                            onClick={() => {
                              setMenuFor(menuFor === g.carrier ? null : g.carrier)
                              setReason(''); setErrorMsg(null)
                            }}
                            aria-label={`Change ${g.carrier}`}
                          >
                            <Icon name="more-horizontal" />
                          </button>
                        )}
                      </td>
                    </tr>

                    {(!g.enabled && g.disabled_reason) || (g.last_run_status === 'FAIL' && g.last_error) || menuFor === g.carrier ? (
                      <tr className="border-b border-ink-100">
                        <td />
                        <td colSpan={6} className="pb-3 -mt-1">
                          {!g.enabled && g.disabled_reason && (
                            <div className="text-[12px] text-honey-600">
                              Switched off{g.disabled_by && <> by {g.disabled_by}</>}
                              {g.disabled_at && <> on {new Date(g.disabled_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                              : {g.disabled_reason}
                            </div>
                          )}
                          {g.last_run_status === 'FAIL' && g.last_error && (
                            <div className="text-[12px] text-flame-600 font-mono truncate">
                              {g.last_error.split('\n').slice(-1)[0]}
                            </div>
                          )}

                          {/* the only way to change state */}
                          {menuFor === g.carrier && (
                            <div className="mt-2 p-3 rounded-lg border border-ink-200 bg-ink-50 max-w-xl">
                              {g.enabled ? (
                                <>
                                  <div className="text-[12px] text-ink-700 mb-2">
                                    Switch {LABEL[g.carrier] ?? g.carrier} off? It will create no rebills until
                                    switched back on. Say why — whoever sees this next needs to know before
                                    turning it back on.
                                  </div>
                                  <input
                                    className="w-full px-2 py-1 text-[12px] border border-ink-200 rounded mb-2 bg-white"
                                    placeholder="e.g. new rules being checked"
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}
                                    autoFocus
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      className="px-3 py-1 text-[12px] rounded bg-flame-600 text-white disabled:opacity-40"
                                      disabled={reason.trim().length < 3 || toggle.isPending}
                                      onClick={() => toggle.mutate({
                                        carrier: g.carrier, enabled: false, reason: reason.trim(),
                                      })}
                                    >
                                      {toggle.isPending ? 'Switching off…' : 'Switch off'}
                                    </button>
                                    <button className="px-3 py-1 text-[12px] rounded border border-ink-200 bg-white"
                                            onClick={() => setMenuFor(null)}>
                                      Cancel
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="text-[12px] text-ink-700 mb-2">
                                    Switch {LABEL[g.carrier] ?? g.carrier} on? Its next run creates rebills for
                                    everything inside its lookback window — including the period it was off.
                                    {g.disabled_reason && (
                                      <div className="mt-1 text-honey-600">
                                        It was switched off because: {g.disabled_reason}
                                      </div>
                                    )}
                                  </div>
                                  {/* Re-enabling is the direction that starts an unsafe
                                      generator, so it needs more than two clicks: type the
                                      carrier code to confirm. */}
                                  <input
                                    className="w-full px-2 py-1 text-[12px] border border-ink-200 rounded mb-2 bg-white"
                                    placeholder={`Type ${g.carrier} to confirm`}
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}
                                    autoFocus
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      className="px-3 py-1 text-[12px] rounded bg-mint-600 text-white disabled:opacity-40"
                                      disabled={toggle.isPending
                                                || reason.trim().toUpperCase() !== g.carrier}
                                      onClick={() => toggle.mutate({
                                        carrier: g.carrier, enabled: true, reason: '',
                                      })}
                                    >
                                      {toggle.isPending ? 'Switching on…' : 'Switch on'}
                                    </button>
                                    <button className="px-3 py-1 text-[12px] rounded border border-ink-200 bg-white"
                                            onClick={() => setMenuFor(null)}>
                                      Cancel
                                    </button>
                                  </div>
                                </>
                              )}
                              {errorMsg && (
                                <div className="mt-2 text-[12px] text-flame-600">{errorMsg}</div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </FragmentRow>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-ink-500">
        <span><span className="pill pill-mint">Running daily</span> scheduled at 06:00, recently ran</span>
        <span><span className="pill pill-sky">On · on upload</span> runs when a file is uploaded</span>
        <span><span className="pill pill-honey">No run recorded yet</span> scheduled, but no recorded run</span>
        <span><span className="pill pill-ink">Off</span> creates nothing</span>
      </div>

      {!isAdmin && rows.length > 0 && (
        <div className="mt-3 text-[12px] text-ink-400">Only an admin can change these.</div>
      )}
    </Card>
  )
}

// Two <tr> per generator (main row + optional detail row) need a keyed wrapper.
function FragmentRow({ children }: { children: ReactNode }) {
  return <>{children}</>
}
